import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Invitation } from "@models/invitation";
import { InviteGuestDTO } from "@models/invite-guest";
import { Page } from "@models/page";
import { PaginatorState } from "primeng/paginator";
import { catchError, of, tap, throwError } from "rxjs";
import { environment } from "../../environments/environment";
import { SnackBarService } from "./snack-bar.service";

@Injectable({
  providedIn: "root",
})
export class InvitationsService {
  private readonly baseUrl = `${environment.API_BACKEND}/invitations`;

  constructor(
    private readonly http: HttpClient,
    private snackbarService: SnackBarService,
  ) {}

  getMyInvitations(pageEvent?: PaginatorState) {
    const page = pageEvent?.page ?? 0;
    const params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", pageEvent?.rows ?? 10);
    return this.http
      .get<Page<Invitation>>(`${environment.API_BACKEND}/my_invitations`, {
        params: params,
      })
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<Invitation>),
        ),
      );
  }

  acceptInvitation(invitation_id: number) {
    return this.http
      .patch<{ message: string }>(`${this.baseUrl}/${invitation_id}/accept`, {})
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite aceito com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          if (err.status === 422) {
            this.snackbarService.openSnackBar(
              err.error?.error || "Não foi possível concluir a operação com o convite.",
              "Fechar",
              "error",
            );
          } else if (err.status === 404) {
            this.snackbarService.openSnackBar(
              "Convite não encontrato",
              "Fechar",
              "error",
            );
          } else {
            this.snackbarService.openSnackBar(
              "Erro ao aceitar convite",
              "Fechar",
              "error",
            );
          }
          return throwError(() => err);
        }),
      );
  }

  declineInvitation(invitation_id: number) {
    return this.http
      .patch<{
        message: string;
      }>(`${this.baseUrl}/${invitation_id}/decline`, {})
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite recusado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          if (err.status === 422) {
            this.snackbarService.openSnackBar(
              err.error?.error || "Não foi possível recusar o convite.",
              "Fechar",
              "error",
            );
          } else if (err.status === 404) {
            this.snackbarService.openSnackBar(
              "Convite não encontrato",
              "Fechar",
              "error",
            );
          } else {
            this.snackbarService.openSnackBar(
              "Erro ao recusar convite",
              "Fechar",
              "error",
            );
          }
          return throwError(() => err);
        }),
      );
  }

  updateInvitations(invitation_id: number, invitation: InviteGuestDTO) {
    return this.http
      .patch<InviteGuestDTO>(`${this.baseUrl}/${invitation_id}`, { invitation })
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite atualizado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          if (err.status === 422) {
            this.snackbarService.openSnackBar(
              err.error?.error || "Não foi possível concluir a operação com o convite.",
              "Fechar",
              "error",
            );
          } else if (err.status === 404) {
            this.snackbarService.openSnackBar(
              "Convite não encontrato",
              "Fechar",
              "error",
            );
          } else {
            this.snackbarService.openSnackBar(
              "Erro ao reenviar convite",
              "Fechar",
              "error",
            );
          }
          return throwError(() => err);
        }),
      );
  }

  deleteInvitations(invitation_id: number) {
    return this.http.delete(`${this.baseUrl}/${invitation_id}`).pipe(
      tap(() =>
        this.snackbarService.openSnackBar(
          "Convite removido com sucesso",
          "Fechar",
          "success",
        ),
      ),
      catchError((err) => {
        if (err.status === 422) {
          this.snackbarService.openSnackBar(
            err.error?.error || "Não foi possível concluir a operação com o convite.",
            "Fechar",
            "error",
          );
        } else if (err.status === 404) {
          this.snackbarService.openSnackBar(
            "Convite não encontrato",
            "Fechar",
            "error",
          );
        } else if (err.status === 403) {
          this.snackbarService.openSnackBar(
            "Você não possui permissão para remover esse convite",
            "Fechar",
            "error",
          );
        } else {
          this.snackbarService.openSnackBar(
            "Erro ao remover convite",
            "Fechar",
            "error",
          );
        }
        return throwError(() => err);
      }),
    );
  }
}
